import React from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SectionHeader from './SectionHeader';
import { C, F } from '../styles/tokens';

const FAQS = [
  {
    q: 'How do I register a company in India?',
    a: 'To register a company in India, choose your business structure (Private Limited, LLP, or OPC), obtain DSC and DIN for directors, reserve your company name on the MCA portal, draft the MOA and AOA, and file SPICe+ form. Pracco handles this entire process end-to-end, and you get your Certificate of Incorporation typically within 7–10 working days.',
  },
  {
    q: 'What is the cost of company registration in India?',
    a: 'Government fees for company registration depend on the authorised capital. For a Private Limited Company with ₹1 lakh authorised capital, government fees are approximately ₹2,000–₹5,000. Professional fees vary. Contact Pracco for a transparent, all-inclusive quote with no hidden charges.',
  },
  {
    q: 'Is GST registration mandatory for new businesses?',
    a: 'GST registration is mandatory if your annual turnover exceeds ₹20 lakh (₹10 lakh for special category states), you supply goods/services interstate, or you sell on e-commerce platforms. Voluntary registration is also possible for businesses below the threshold.',
  },
  {
    q: 'What is the difference between Private Limited Company and LLP?',
    a: 'A Private Limited Company is ideal for startups seeking investment, as it allows issuing equity shares to investors. An LLP (Limited Liability Partnership) has simpler compliance requirements and lower annual costs, making it suitable for professional service firms, consultancies, and small businesses. Both offer limited liability protection.',
  },
  {
    q: 'Can I register my company online in India?',
    a: 'Yes. Company registration in India is fully online through the MCA21 portal using the SPICe+ form. Pracco manages the entire online registration process — from name approval and document submission to obtaining your incorporation certificate — without you needing to visit any government office.',
  },
  {
    q: 'What are the annual compliance requirements after company registration?',
    a: 'After company registration, annual compliances include: filing Annual Return (MGT-7) and Financial Statements (AOC-4) with MCA, conducting Annual General Meeting (AGM), maintaining statutory registers, filing income tax return, and GST returns if registered. Pracco offers annual compliance packages for stress-free post-registration management.',
  },
];

const FAQSection = () => (
  <Box
    component="section"
    id="faq"
    sx={{ px: { xs: 2, md: 8 }, py: { xs: 7, md: 10 }, background: C.bg }}
  >
    <SectionHeader
      label="FAQs"
      title="Frequently Asked Questions"
      subtitle="Answers to the most common questions about company registration, GST, and tax filing in India."
    />

    {/* JSON-LD FAQ schema for Google rich results */}
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      }}
    />

    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      {FAQS.map((faq) => (
        <Accordion
          key={faq.q}
          disableGutters
          elevation={0}
          sx={{
            border:        `1px solid ${C.border}`,
            mb:            1.5,
            borderRadius:  '12px !important',
            background:    C.white,
            '&:before':    { display: 'none' },
            '&:hover':     { borderColor: C.blue },
            transition:    'border-color 0.2s',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: C.blue }} />}
            sx={{ px: 3, py: 0.5 }}
          >
            <Typography
              component="h3"
              sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: { xs: 14, md: 15 }, color: C.text, lineHeight: 1.4 }}
            >
              {faq.q}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
            <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.textMid, lineHeight: 1.85 }}>
              {faq.a}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  </Box>
);

export default FAQSection;
